import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import { Box, Typography, Card, CardContent } from '@mui/material';
import VariableAutocomplete from '../VariableAutocomplete';
import PanelShell from './PanelShell';

const ForPanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();
  const vars = useSelector((state) => state.flow.vars) || [];
  const tags = useSelector((state) => state.flow.tags) || [];
  const localVars = useSelector((state) => state.flow.localVars) || [];

  const [focusedInput, setFocusedInput] = useState(null);
  const [parameterMappings, setParameterMappings] = useState({});

  // Parse variable string to get proper variable object
  const parseVariableString = (value, inputType) => {
    if (!value) return null;

    if (inputType === 'constant' || inputType === 'text') {
      return {
        variableId: value,
        variableName: value,
        variableType: 'constant'
      };
    }

    if (value.startsWith('lv_')) {
      const varName = value.substring(3);
      const foundLocalVar = localVars.find(v => v.name === varName);
      return {
        variableId: value,
        variableName: foundLocalVar ? foundLocalVar.name : value,
        variableType: 'LV'
      };
    }

    if (value.startsWith('var_')) {
      const varId = value.substring(4);
      const foundVar = vars.find(v => {
        const varIdValue = v.varId !== undefined ? v.varId : v.varID;
        return String(varIdValue) === String(varId);
      });
      return {
        variableId: value,
        variableName: foundVar ? foundVar.varName : value,
        variableType: 'V'
      };
    }

    if (value.startsWith('tag_')) {
      const tagId = value.substring(4);
      const foundTag = tags.find(t => {
        const tagIdValue = t.tagId !== undefined ? t.tagId : t.tagID;
        return String(tagIdValue) === String(tagId);
      });
      return {
        variableId: value,
        variableName: foundTag ? foundTag.tagName : value,
        variableType: 'T'
      };
    }

    return {
      variableId: value,
      variableName: value,
      variableType: 'constant'
    };
  };

  // Initialize data when component mounts
  useEffect(() => {
    if (!data) return;

    const newMappings = {
      variable: parseVariableString(data.selectedVariable, data.inputType),
      start: parseVariableString(data.selectedVariable1 || data.start, data.inputType1),
      end: parseVariableString(data.selectedVariable2 || data.end, data.inputType2),
      step: parseVariableString(data.selectedVariable3 || data.step, data.inputType3)
    };

    console.log('Initialized For Mappings:', newMappings);
    setParameterMappings(newMappings);
  }, [data, vars, tags, localVars]);

  const getColorForType = (type) => {
    switch(type) {
      case 'V': return '#fff3e0';
      case 'T': return '#edbc45';
      case 'LV': return '#f3e5f5';
      case 'constant': return '#e0e0e0';
      default: return '#f5f5f5';
    }
  };

  const handleVariableSelect = (event, variable) => {
    if (!focusedInput || !variable) return;

    let variableId;
    let variableName;
    const variableType = variable.type;

    switch (variableType) {
      case 'LV':
        variableId = `lv_${variable.name}`;
        variableName = variable.name;
        break;
      case 'V':
        const varIdValue = variable.varID || variable.varId || variable.id;
        variableId = `var_${varIdValue}`;
        variableName = variable.varName || variable.name;
        break;
      case 'T':
        const tagIdValue = variable.tagID || variable.tagId || variable.id;
        variableId = `tag_${tagIdValue}`;
        variableName = variable.tagName || variable.name;
        break;
      case 'constant':
        variableId = variable.name;
        variableName = variable.name;
        break;
      default:
        variableId = variable.id;
        variableName = variable.name;
    }

    setParameterMappings(prev => ({
      ...prev,
      [focusedInput]: {
        variableId,
        variableName,
        variableType
      }
    }));
  };

  const InputCard = ({ label, inputKey, mapping }) => {
    const isFocused = focusedInput === inputKey;

    return (
      <Card
        onClick={() => setFocusedInput(inputKey)}
        sx={{
          cursor: 'pointer',
          border: isFocused ? '2px solid #6a1b9a' : '1px solid #ddd',
          backgroundColor: isFocused ? '#f3e5f5' : 'white',
          mb: 1
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="body2" fontWeight={600} mb={0.5} color="text.secondary">
            {label}
          </Typography>

          {mapping?.variableName ? (
            <Box sx={{
              p: 1,
              backgroundColor: getColorForType(mapping.variableType),
              borderRadius: 1,
              fontSize: '0.9rem'
            }}>
              {mapping.variableName}
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              Değişken seçilmedi
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleSave = () => {
    const { variable, start, end, step } = parameterMappings;

    if (!variable || !start || !end || !step) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen tüm alanları doldurun.'
      }));
      return;
    }

    const title = `For ${variable.variableName} from ${start.variableName} to ${end.variableName} step ${step.variableName}`;

    const nodeData = {
      selectedVariable: variable.variableId,
      selectedVariable1: start.variableId,
      selectedVariable2: end.variableId,
      selectedVariable3: step.variableId,
      inputType: variable.variableType === 'constant' ? 'constant' : 'variable',
      inputType1: start.variableType === 'constant' ? 'constant' : 'variable',
      inputType2: end.variableType === 'constant' ? 'constant' : 'variable',
      inputType3: step.variableType === 'constant' ? 'constant' : 'variable',
      // Backward compatibility
      start: start.variableId,
      end: end.variableId,
      step: step.variableId,
      label: 'For',
      Title: title,
    };

    console.log('Saving For Node Data:', nodeData);

    onChange(nodeData);
    dispatch(isModalOpen());
    dispatch(setToastMessage({
      type: 'success',
      message: 'For döngüsü kaydedildi.'
    }));
  };

  if (nodeType !== "For") return null;

  return (
    <PanelShell title="For Döngüsü" onSave={handleSave}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Variable Selector */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Değişken / Sabit Seçimi
          </Typography>
          <VariableAutocomplete
            value={null}
            onChange={handleVariableSelect}
            placeholder="Tag, değişken veya sabit ara..."
            showConstants={true}
          />
          {focusedInput && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
              Seçilen alan: {focusedInput === 'variable' ? 'Döngü Değişkeni' : focusedInput === 'start' ? 'Başlangıç Değeri' : focusedInput === 'end' ? 'Bitiş Değeri' : 'Adım'}
            </Typography>
          )}
        </Box>

        {/* Input Parameters */}
        <Box>
          <InputCard
            label="Döngü Değişkeni"
            inputKey="variable"
            mapping={parameterMappings.variable}
          />

          <InputCard
            label="Başlangıç Değeri"
            inputKey="start"
            mapping={parameterMappings.start}
          />

          <InputCard
            label="Bitiş Değeri"
            inputKey="end"
            mapping={parameterMappings.end}
          />

          <InputCard
            label="Adım"
            inputKey="step"
            mapping={parameterMappings.step}
          />
        </Box>
      </Box>
    </PanelShell>
  );
};

export default ForPanel;
